/**
 * Shipments panel (procurement.md §3/§4): each order's shipments with a tracking link, status and ETA, plus a manual
 * paste. Vendor-email parsing and the tracking API fill the rest server-side; nothing here is inferred.
 */
import { Button, Chip, type ChipTone, Input, Picker, toast } from '@patchbay/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Truck } from 'lucide-react'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import { formatWhen } from '../../lib/display-time'
import { safeHref } from '../../lib/safeUrl'
import { useMe } from '../auth/session'
import { type Carrier, type HardwareOrderOut, procurementApi, procurementKeys, type ShipmentOut } from './api'
import { isOverdue } from './status'
import {
  CARRIER_LABEL,
  carrierPrompt,
  formatEta,
  guessCarrier,
  needsCarrierChoice,
  nextShipmentDestination,
  orderedCarriers,
  shipmentStatusText,
  suggestsPurolatorFirst,
  trackingUrl,
} from './tracking'

const STATUS_TONE: Record<ShipmentOut['status'], ChipTone> = {
  label_created: 'neutral',
  in_transit: 'primary',
  out_for_delivery: 'primary',
  delivered: 'success',
  exception: 'danger',
  unknown: 'outline',
}

const SOURCE_LABEL: Record<ShipmentOut['source'], string> = {
  email: 'from the vendor email',
  api: 'from carrier tracking',
  manual: 'entered by hand',
}

const DESTINATION_LABEL = {
  office: 'Ewee office',
  customer_site: 'Customer site',
} as const

function shortDate(value: string | null | undefined): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return formatWhen(date.toISOString(), { style: 'date' })
}

function legOrigin(order: HardwareOrderOut, shipment: ShipmentOut): string {
  if (shipment.leg_index <= 1 || shipment.destination === 'office') return 'Vendor'
  const previous = order.shipments.find((item) => item.leg_index === shipment.leg_index - 1)
  return previous?.destination === 'office' ? 'Ewee' : 'Vendor'
}

function ShipmentRow({ s, order, canWrite }: { s: ShipmentOut; order: HardwareOrderOut; canWrite: boolean }) {
  const qc = useQueryClient()
  const href = trackingUrl(s)
  const safe = href ? safeHref(href) : null
  const late = s.status !== 'delivered' && isOverdue(s.eta)
  const eta = formatEta(s.eta)
  const untracked = s.tracking_automatic !== true && s.status === 'label_created'
  const [editing, setEditing] = React.useState(false)
  const [number, setNumber] = React.useState(s.tracking_number)
  const [carrier, setCarrier] = React.useState<Carrier>(s.carrier)
  const [destination, setDestination] = React.useState(s.destination)
  const [note, setNote] = React.useState(s.note ?? '')
  const [busy, setBusy] = React.useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await procurementApi.editShipment(s.id, {
        carrier,
        tracking_number: number.trim(),
        destination,
        note: note.trim() || null,
      })
      setEditing(false)
      await qc.invalidateQueries({ queryKey: ['procurement'] })
      toast.success('Tracking updated')
    } catch (error) {
      toast.error(`Tracking not updated: ${describeError(error)}`)
    } finally {
      setBusy(false)
    }
  }

  const markDelivered = async () => {
    setBusy(true)
    try {
      await procurementApi.markDelivered(s.id)
      await qc.invalidateQueries({ queryKey: ['procurement'] })
      toast.success('Marked delivered')
    } catch (error) {
      toast.error(`Delivery not recorded: ${describeError(error)}`)
    } finally {
      setBusy(false)
    }
  }

  const checkedIn = order.lines.length > 0 && order.lines.every((line) => line.qty_received >= line.qty)
  const delivered = s.status === 'delivered' ? shortDate(s.last_event_at) : null
  const stage =
    s.destination === 'office' && s.status === 'delivered'
      ? checkedIn
        ? 'Received/staged'
        : 'Awaiting office receipt'
      : s.destination === 'customer_site' && s.status === 'delivered'
        ? 'At customer site'
        : null

  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border border-border px-2.5 py-2 text-sm" data-shipment={s.id}>
      <Truck className="size-4 text-muted-foreground" aria-hidden />
      <span className="font-medium">
        Leg {s.leg_index} · {legOrigin(order, s)} → {DESTINATION_LABEL[s.destination]}
      </span>
      <span className="text-xs text-muted-foreground">{CARRIER_LABEL[s.carrier]}</span>
      {safe ? (
        <a
          href={safe}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
        >
          {s.tracking_number} <ExternalLink className="size-3" aria-hidden />
          <span className="sr-only">(track on the carrier site)</span>
        </a>
      ) : (
        <span className="font-mono text-xs">{s.tracking_number}</span>
      )}
      {untracked ? (
        <span className="text-xs text-muted-foreground">{shipmentStatusText(s)}</span>
      ) : (
        <Chip tone={late ? 'danger' : STATUS_TONE[s.status]}>{late ? 'Overdue' : shipmentStatusText(s)}</Chip>
      )}
      <span className="ml-auto text-xs text-muted-foreground">
        {s.status === 'delivered' ? `Delivered${delivered ? ` ${delivered}` : ''}` : eta ? `ETA ${eta}` : 'No ETA yet'}
        {' · '}
        {SOURCE_LABEL[s.source]}
      </span>
      {stage ? <Chip tone={stage === 'Received/staged' || stage === 'At customer site' ? 'success' : 'warning'}>{stage}</Chip> : null}
      {s.note ? <p className="basis-full text-xs text-muted-foreground">{s.note}</p> : null}
      {canWrite && !editing ? (
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          Edit tracking
        </Button>
      ) : null}
      {canWrite && s.tracking_automatic !== true && s.status !== 'delivered' ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void markDelivered()}>
          Mark delivered
        </Button>
      ) : null}
      {editing ? (
        <form
          className="flex w-full flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (number.trim()) void save()
          }}
        >
          <Input
            aria-label={`Tracking number for ${s.tracking_number}`}
            value={number}
            onChange={(event) => setNumber(event.currentTarget.value)}
            className="min-w-40 flex-1 font-mono"
          />
          <Picker
            aria-label={`Carrier for ${s.tracking_number}`}
            value={carrier}
            onChange={(event) => setCarrier(event.currentTarget.value as Carrier)}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm"
          >
            {(Object.keys(CARRIER_LABEL) as Carrier[]).map((item) => (
              <option key={item} value={item}>
                {CARRIER_LABEL[item]}
              </option>
            ))}
          </Picker>
          <Picker
            aria-label={`Destination for leg ${s.leg_index}`}
            value={destination}
            onChange={(event) => setDestination(event.currentTarget.value as ShipmentOut['destination'])}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="office">Ewee office</option>
            <option value="customer_site">Customer site</option>
          </Picker>
          <Input
            aria-label={`Note for leg ${s.leg_index}`}
            placeholder="Optional note"
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
            className="min-w-40 flex-1"
          />
          <Button size="sm" type="submit" disabled={!number.trim() || busy}>
            Save tracking
          </Button>
          <Button size="sm" type="button" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      ) : null}
    </li>
  )
}

export function ShipmentsPanel({ order, canAdd = true }: { order: HardwareOrderOut; canAdd?: boolean }) {
  const qc = useQueryClient()
  const me = useMe()
  const canWrite = Boolean(me && me.role !== 'billing')
  const [number, setNumber] = React.useState('')
  const [carrier, setCarrier] = React.useState<Carrier | ''>('')
  const suggestedDestination = nextShipmentDestination(order)
  const [destination, setDestination] = React.useState<'office' | 'customer_site'>(suggestedDestination)
  const [note, setNote] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const ambiguous = number ? needsCarrierChoice(number) : false
  const guessed = number && !ambiguous ? guessCarrier(number) : null
  const preferPurolator = suggestsPurolatorFirst(order)
  const prompt = carrierPrompt(preferPurolator, ambiguous, guessed)
  React.useEffect(() => setDestination(suggestedDestination), [suggestedDestination])

  const add = async () => {
    setBusy(true)
    try {
      await procurementApi.addShipment({
        hardware_order_id: order.id,
        case_id: order.case_id,
        text: number.trim(),
        carrier: carrier || guessed || null,
        destination,
        note: note.trim() || null,
      })
      setNumber('')
      setCarrier('')
      setNote('')
      await Promise.all([
        qc.invalidateQueries({ queryKey: procurementKeys.case(order.case_id) }),
        qc.invalidateQueries({ queryKey: procurementKeys.order(order.id) }),
      ])
      toast.success('Tracking number added')
    } catch (error) {
      toast.error(`Tracking number not saved: ${describeError(error)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-label="Shipments" className="space-y-2">
      <h4 className="text-xs font-semibold tracking-wide text-subtle uppercase">Shipments</h4>
      {order.shipments.length ? (
        <ul className="space-y-1.5">
          {order.shipments.map((s) => (
            <ShipmentRow key={s.id} s={s} order={order} canWrite={canWrite} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No shipment recorded yet. Tracking numbers arrive from the vendor's emails on this case, or paste one below.
        </p>
      )}
      {canAdd && canWrite ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (number.trim() && !(ambiguous && !carrier)) void add()
          }}
        >
          <Input
            aria-label="Tracking number"
            placeholder="Paste a tracking number"
            value={number}
            onChange={(e) => setNumber(e.currentTarget.value)}
            className="min-w-40 flex-1 font-mono"
          />
          <Picker
            aria-label="Carrier"
            value={carrier}
            onChange={(e) => setCarrier(e.currentTarget.value as Carrier | '')}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="">{prompt}</option>
            {orderedCarriers(preferPurolator, ambiguous).map((item) => (
              <option key={item} value={item}>
                {CARRIER_LABEL[item]}
              </option>
            ))}
          </Picker>
          <Picker
            aria-label="Shipment destination"
            value={destination}
            onChange={(e) => setDestination(e.currentTarget.value as 'office' | 'customer_site')}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="office">Ewee office</option>
            <option value="customer_site">Customer site</option>
          </Picker>
          <Input
            aria-label="Shipment note"
            placeholder="Optional note"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            className="min-w-40 flex-1"
          />
          <Button size="sm" type="submit" disabled={!number.trim() || busy || (ambiguous && !carrier)}>
            {order.shipments.length ? 'Add another shipment' : 'Add shipment'}
          </Button>
        </form>
      ) : null}
    </section>
  )
}
